import { isEqual } from 'lodash';
import { isActiveGroup } from 'sonata-static-storage/lib/helpers/itemsGrouping.helpers';

import { toId } from './groupIds';

import type { GroupLike } from './grouping.types';

const byId = <G extends GroupLike>(groups: G[]): Map<string, G> =>
  new Map(
    groups.flatMap(group => {
      const id = toId(group._id);
      return id ? [[id, { ...group, _id: id }]] : [];
    })
  );

/**
 * Venue groups live on the user: the session buffer only holds groups that are new or differ from the
 * persisted copy (including soft deletes). Groups absent from `next` (hard deleted) simply vanish.
 */
export function toVenueGroupsBuffer<G extends GroupLike>(persisted: GroupLike[], next: G[]): G[] {
  const persistedById = byId(persisted);
  return [...byId(next).entries()].filter(([id, group]) => !isEqual(persistedById.get(id), group)).map(([, g]) => g);
}

/**
 * Merges the session buffer over the persisted user venue groups for `putUser`: edits and soft deletes
 * replace the persisted copy, new groups are appended, and never-persisted deleted groups are dropped.
 */
export function mergeVenueGroupsForSave<G extends GroupLike>(
  persisted: G[],
  buffer: G[]
): { changed: boolean; groups: G[] } {
  const merged = byId(persisted);
  let changed = false;
  byId(buffer).forEach((group, id) => {
    const existing = merged.get(id);
    if ((!existing && !isActiveGroup(group)) || isEqual(existing, group)) {
      return;
    }
    merged.set(id, group);
    changed = true;
  });
  return { changed, groups: [...merged.values()] };
}
